import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import masterDataService from '../../../service/masterData.service';
import { generateAutocompleteItems } from '../../../utils/helper';
import usePages from '../../../context/PageContext';
import useMenus from '../../../context/MenuContext';
import useUsers from '../../../context/UsersContext';
import useParam from '../../../context/ParamContext';
import productionService from '../../../service/production.service';
import jobMonitorService from '../../../service/jobMonitor.service';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import useSaveView from '../../../hooks/useSaveView';
import useView from '../../../hooks/useView';
import { itemAutocompleteRequestBuilder, itemAutocompleteService } from '../../../utils/itemAutocomplete';

import {
  AttachmentDialogUI,
  AutoCompleteSelectUI,
  AutoCompleteSelectServiceUI,
  ButtonUI,
  DebouncedInputUI,
  FormLabelUI,
  SwitchUI,
} from '../../Interface';
import JobMonitorCard from './JobMonitorCard';
import ProductDialog from './ProductDialog';
import JobMonitorResources from './JobMonitorResources';
import resourceOneService from '../../../service/resources/resourceOne.service';
import TaskFeedbackDialog from '../../TaskFeedback/TaskFeedbackDialog';
import sourcelistService from '../../../service/sourcelist.service';
import { Typography } from '@mui/material';

export const NewJobMonitorTaskAssigmentPage = () => {
  const RENDER_CHUNK_SIZE = 40;
  const navigate = useNavigate();
  const { getUser, user } = useUsers();
  const { getParam, params } = useParam();
  const saveView = useSaveView(user);
  const jobMonitorTaskAssigmentFilterV2 = useView('jobMonitorTaskAssigmentFilterV2', user);
  const { menus, getMenus, getMenuSubMenuId, getSubMenuNamesByUrls } = useMenus();
  const { setPages } = usePages();
  const [prodOperations, setProdOperations] = useState({ rows: [], rowCount: 0 });
  const [submenu, setSubmenu] = useState(null);
  const [production, setProduction] = useState({});
  const [productDialogIsOpen, setProductDialogIsOpen] = useState(false);
  const [draggingItemId, setDraggingItemId] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState({ resourceId: null, index: null });
  const [autocompleteOptions, setAutoCompleteOptions] = useState({
    resourceOne: [],
    operations: [],
    status: [],
  });
  const [autoCompleteData, setAutoCompleteData] = useState({});
  const [resources, setResources] = useState([]);
  const [selectedResources, setSelectedResources] = useState({ id: [] });
  const [check, setCheck] = useState(false);
  const [prevStatus, setPrevStatus] = useState(false);
  const [filteredResources, setFilteredResources] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [isAttachmentDialogOpen, setIsAttachmentDialogOpen] = useState(false);
  const [isFeedBackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const [feedbackData, setFeedbackData] = useState({});
  const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);
  const [productionChanged, setProductionChanged] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [renderedOperationCount, setRenderedOperationCount] = useState(RENDER_CHUNK_SIZE);
  const chunkAppendScheduledRef = useRef(false);

  const [query, setQuery] = useState({
    filter: {
      filterModel: {
        items: [],
        logicOperator: 'and',
      },
    },
    sorting: {
      sortModel: [{ field: 'BefejezesiHatarido', sort: 'asc' }],
    },
  });

  useEffect(() => {
    getMenus();
    getParam();
    if (!Object.keys(user).length) getUser();
  }, []);

  useEffect(() => {
    const filterItems = query?.filter?.filterModel?.items ?? [];
    const hasProductionStatusFilter = filterItems.some((item) => item.field === 'prodOpStatusId' && item.value);

    if (
      Object.keys(user).length > 0 &&
      filterItems.length > 0 &&
      selectedResources?.id.length > 0 &&
      hasProductionStatusFilter
    ) {
      getList();
    }
    setProductionChanged(false);
  }, [
    user,
    selectedResources?.id,
    productionChanged,
    JSON.stringify(query?.filter?.filterModel?.items),
    JSON.stringify(query?.sorting?.sortModel),
  ]);

  useEffect(() => {
    const filterItems = query?.filter?.filterModel?.items ?? [];
    const hasProductionStatusFilter = filterItems.some((item) => item.field === 'prodOpStatusId' && item.value);

    if (selectedResources?.id.length > 0 && autoCompleteData?.resourceOne && hasProductionStatusFilter) {
      getResourcesByGroupId();
    } else if (selectedResources?.id.length === 0) {
      setResources([]);
      setProdOperations({ rows: [], rowCount: 0 });
      setRenderedOperationCount(RENDER_CHUNK_SIZE);
    }
    setProductionChanged(false);
  }, [JSON.stringify(query.filter.filterModel.items), autoCompleteData?.resourceOne, productionChanged]);

  const renderedProdOperations = useMemo(() => {
    return (prodOperations.rows ?? []).slice(0, renderedOperationCount);
  }, [prodOperations.rows, renderedOperationCount]);

  const getRowKey = (row) => `${row.productionoperationid}-${row.multiresourceId ?? 'main'}`;

  const selectedItemKeys = useMemo(() => {
    const keys = new Set();
    for (const item of selectedItems) {
      keys.add(getRowKey(item));
    }
    return keys;
  }, [selectedItems]);

  useEffect(() => {
    const totalRows = prodOperations.rows?.length ?? 0;
    setRenderedOperationCount((prev) => Math.min(prev, totalRows));
  }, [prodOperations.rows]);

  useEffect(() => {
    if (submenu) {
      initDefaultValues();
    }
  }, [submenu]);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('production');
    setSubmenu(submenuId);
  }, [menus]);

  useEffect(() => {
    if (resources?.length > 0) {
      if (check) {
        const filtered = resources.map((group) => {
          const filteredRows = group.rows.filter((row) => row.prodOpStatusId === 'COMPLETED');
          return {
            ...group,
            rows: filteredRows,
          };
        });

        setFilteredResources(filtered);
      } else {
        setFilteredResources(resources);
      }
    }
  }, [check, resources]);

  useEffect(() => {
    if (Object.keys(jobMonitorTaskAssigmentFilterV2).length > 0 && !isInitialLoadDone) {
      const { finishedItemCode, ...filteredFilter } = jobMonitorTaskAssigmentFilterV2;

      if ('resourceOneId' in filteredFilter && filteredFilter.resourceOneId?.length > 0) {
        setSelectedResources((prev) => ({
          ...prev,
          id: filteredFilter.resourceOneId,
        }));
      }

      const filters = Object.entries(filteredFilter).map(([field, value]) => ({ field, value }));
      if (filters.length > 0) {
        handleFilterChange(filters);
      }

      setIsInitialLoadDone(true);
    }
  }, [jobMonitorTaskAssigmentFilterV2, isInitialLoadDone]);

  useEffect(() => {
    if (selectedResources.id?.length > 0 && params.JOBMONITOR_STATUSES && isInitialLoadDone) {
      updateQueryForStatusId(false);
    }
  }, [selectedResources.id, params, query.filter.filterModel.items, isInitialLoadDone]);

  const submenuNames = useMemo(() => {
    return getSubMenuNamesByUrls(['productionreport']);
  }, [menus]);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .resourceOneToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .operationToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .productionStatusToForm(submenu)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          resourceOne: resp[0],
          operations: resp[1],
          status: resp[2],
        };

        setAutoCompleteData(aData);
        const filteredResourceOne = aData.resourceOne?.filter((r1) => r1.name.startsWith('PUFFER'));

        const acOptions = {
          resourceOne: generateAutocompleteItems(filteredResourceOne, 'name', 'id'),
          operations: generateAutocompleteItems(aData.operations, 'operationName', 'operationId'),
          status: generateAutocompleteItems(aData.status, 'name', 'code'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {});
  };

  const getResourcesByGroupId = () => {
    const resourceOneId = selectedResources?.id?.map((r) => r.value) ?? [];

    const matchedResources = autoCompleteData?.resourceOne?.filter((r1) => resourceOneId.includes(r1.id)) ?? [];

    const groupId = Array.from(
      new Set(
        matchedResources
          .flatMap(
            (r) => r.operation?.flatMap((op) => op.operationGroupLink?.map((link) => link.operationGroup?.id)) ?? []
          )
          .filter(Boolean)
      )
    );

    const data = {
      filter: {
        ...query.filter,
        filterModel: {
          ...query.filter.filterModel,
          items: [
            ...query.filter.filterModel.items,
            {
              field: 'groupId',
              operator: 'isAnyOf',
              value: groupId,
            },
          ],
        },
      },
      sorting: {
        sortModel: [{ field: 'resourceOneName', sort: 'asc' }],
      },
    };

    if (groupId.length) {
      resourceOneService.getResourcesByGroupIdView(data).then((data) => {
        if (data.length > 0) {
          setResources(data);
        } else {
          setFilteredResources([]);
        }
      });
    }
  };

  const getList = () => {
    jobMonitorService.getProductionOperationsFromView(query).then((data) => {
      setProdOperations(data);
      setRenderedOperationCount(Math.min(RENDER_CHUNK_SIZE, data?.rows?.length ?? 0));
    });
  };

  const resetToFirstPage = (nextQueryPatch = null) => {
    setRenderedOperationCount(RENDER_CHUNK_SIZE);
    setProdOperations({ rows: [], rowCount: 0 });

    if (nextQueryPatch) {
      setQuery((prev) => nextQueryPatch(prev));
      return;
    }

    setQuery((prev) => ({
      filter: {
        ...prev.filter,
        filterModel: {
          ...prev.filter.filterModel,
          items: [...prev.filter.filterModel.items],
        },
      },
      sorting: {
        ...prev.sorting,
      },
    }));
  };

  const updateQueryForStatusId = (usePreviousJobs) => {
    if (!params || !params.JOBMONITOR_STATUSES || selectedResources.id?.length === 0) {
      return;
    }

    const filterItems = query?.filter?.filterModel?.items ?? [];
    const hasProductionStatusFilter = filterItems.some((item) => item.field === 'prodOpStatusId' && item.value);

    if (hasProductionStatusFilter) return;

    const statusConfig = JSON.parse(params.JOBMONITOR_STATUSES);
    const statusIds = !usePreviousJobs && statusConfig.noPreviousWork;
    const newItems = query.filter.filterModel.items
      .filter((item) => item.field !== 'prodOpStatusId')
      .concat({
        field: 'prodOpStatusId',
        operator: 'isAnyOf',
        value: statusIds,
      });

    if (JSON.stringify(newItems) !== JSON.stringify(query.filter.filterModel.items)) {
      resetToFirstPage((prev) => ({
        ...prev,
        filter: {
          ...prev.filter,
          filterModel: {
            ...prev.filter.filterModel,
            items: newItems,
          },
        },
      }));
    }
  };

  const handleFilterChange = (field, value) => {
    resetToFirstPage((prev) => {
      let newItems = prev.filter.filterModel.items;

      if (Array.isArray(field)) {
        newItems = newItems.filter((item) => !field.some((f) => f.field === item.field));

        const validFilters = field
          .filter((f) => {
            const v = f.value;
            return v !== null && v !== undefined && v !== '' && (!Array.isArray(v) || v.length > 0);
          })
          .map((f) => {
            const rawValue = f.value?.value ?? f.value;
            const processedValue = Array.isArray(rawValue) ? rawValue.map((v) => v.value ?? v) : rawValue;

            return {
              field: f.field,
              operator: Array.isArray(processedValue) ? 'isAnyOf' : 'contains',
              value: processedValue,
            };
          });

        newItems = newItems.concat(validFilters);
      } else {
        newItems = newItems.filter((item) => item.field !== field);
        if (value !== null && value !== undefined && value !== '') {
          const processedValue = Array.isArray(value) ? value.map((v) => v.value ?? v) : value;

          newItems.push({
            field,
            operator: Array.isArray(processedValue) ? 'isAnyOf' : 'contains',
            value: processedValue,
          });
        }
      }

      return {
        ...prev,
        filter: {
          filterModel: {
            ...prev.filter.filterModel,
            items: newItems,
          },
        },
      };
    });
  };

  const handleNavigate = (prodOperationId) => {
    setPages({ subMenuName: submenuNames });
    navigate(`/app/productionReport/${prodOperationId}`);
  };

  const getProductionById = (id) => {
    productionService.getProductionById(id).then((data) => {
      setProduction(data);
      setProductDialogIsOpen(true);
    });
  };

  const sameRow = (a, b) =>
    a.productionoperationid === b.productionoperationid && (a.multiresourceId ?? null) === (b.multiresourceId ?? null);

  const updateStateAfterAssignment = (jobItem, newResourceOneId) => {
    setProdOperations((prev) => ({
      ...prev,
      rows: prev.rows.filter((r) => !sameRow(r, jobItem)),
    }));

    setResources((prev) =>
      prev.map((group) => {
        if (group.resourceOneId === jobItem.resourceOneId) {
          return {
            ...group,
            rows: group.rows.filter((r) => !sameRow(r, jobItem)),
          };
        }
        if (group.resourceOneId === newResourceOneId) {
          const updatedRow = { ...jobItem, resourceOneId: newResourceOneId };
          const withoutOld = group.rows.filter((r) => !sameRow(r, jobItem));
          return { ...group, rows: [updatedRow, ...withoutOld] };
        }
        return group;
      })
    );
  };

  const updateStateToUnassigned = (jobItem, resourceOneId) => {
    const updatedRow = { ...jobItem, resourceOneId };
    setProdOperations((prev) => {
      const exists = prev.rows.some((r) => sameRow(r, updatedRow));
      return { ...prev, rows: exists ? prev.rows : [updatedRow, ...prev.rows] };
    });
    setResources((prev) =>
      prev.map((group) =>
        group.resourceOneId === jobItem.resourceOneId
          ? { ...group, rows: group.rows.filter((r) => !sameRow(r, jobItem)) }
          : group
      )
    );
  };

  const handleDropAndResourceUpdate = (resourceOneId, jobItem) => {
    productionService.updateProdOperationResource(jobItem.productionoperationid, resourceOneId).then(() => {
      updateStateAfterAssignment(jobItem, resourceOneId);
    });
  };

  const handleDropAndMultiResourceUpdate = (newResourceOneId, jobItem) => {
    productionService.updateProductionOperationMultiResource(newResourceOneId, jobItem.multiresourceId).then(() => {
      updateStateAfterAssignment(jobItem, newResourceOneId);
    });
  };

  const onDragStart = (event, jobItem) => {
    event.dataTransfer.setData('jobItem', JSON.stringify(jobItem));
    setDraggingItemId(jobItem.productionoperationid);

    const bulkItems = selectedItems.filter(
      (r) =>
        r.productionoperationid === jobItem.productionoperationid &&
        (r.multiresourceId ?? null) === (jobItem.multiresourceId ?? null)
    ).length
      ? selectedItems
      : [];

    if (bulkItems.length > 1) {
      event.dataTransfer.setData('bulkJobItems', JSON.stringify(bulkItems));
    } else {
      try {
        event.dataTransfer.clearData('bulkJobItems');
      } catch (_) {}
    }
  };

  const onDrop = (event, resourceOne) => {
    event.preventDefault();

    const bulkRaw = event.dataTransfer.getData('bulkJobItems');

    if (bulkRaw) {
      const bulkItems = JSON.parse(bulkRaw);

      const body = bulkItems.map((item) => ({
        id: item.productionoperationid,
        resourceOneId: resourceOne.resourceOneId,
      }));

      productionService.updateProdOperationResources(body).then(() => {
        setProdOperations((prev) => ({
          ...prev,
          rows: prev.rows.filter((r) => !bulkItems.some((b) => sameRow(r, b))),
        }));

        setResources((prev) =>
          prev.map((group) => {
            const withoutBulk = group.rows.filter((r) => !bulkItems.some((b) => sameRow(r, b)));

            if (group.resourceOneId === resourceOne.resourceOneId) {
              const updatedRows = bulkItems.map((jobItem) => ({
                ...jobItem,
                resourceOneId: resourceOne.resourceOneId,
              }));

              return {
                ...group,
                rows: [...updatedRows, ...withoutBulk],
              };
            }

            return {
              ...group,
              rows: withoutBulk,
            };
          })
        );

        setSelectedItems([]);
      });

      return;
    }

    const jobItem = JSON.parse(event.dataTransfer.getData('jobItem'));

    const isGroupLeader = resourceOne.webUser?.find(
      (user) => user.resourceOneId === resourceOne.resourceOneId
    )?.groupLeader;

    if (
      jobItem.groupReporting === 0 &&
      jobItem.multiManOperation > 1 &&
      isGroupLeader &&
      jobItem.multiresourceId === null
    ) {
      handleDropAndResourceUpdate(resourceOne.resourceOneId, jobItem);
    } else if (
      jobItem.groupReporting !== 0 &&
      jobItem.multiManOperation > 1 &&
      !isGroupLeader &&
      jobItem.multiresourceId === null
    ) {
      addNotification({
        content: 'A művelet csak csoportvezetőre helyezhető!',
        type: NotificationType.WARNING,
      });
    } else if (jobItem.multiresourceId) {
      handleDropAndMultiResourceUpdate(resourceOne.resourceOneId, jobItem);
    } else {
      handleDropAndResourceUpdate(resourceOne.resourceOneId, jobItem);
    }
  };

  const onDropToUnassigned = (event) => {
    event.preventDefault();

    const bulkRaw = event.dataTransfer.getData('bulkJobItems');

    if (bulkRaw) {
      const bulkItems = JSON.parse(bulkRaw);

      const body = bulkItems.map((jobItem) => ({
        id: jobItem.productionoperationid,
        resourceOneId: jobItem.pufferResourceOneId,
        multiresourceId: jobItem.multiresourceId,
      }));

      productionService.updateProdOperationResources(body).then(() => {
        for (const item of body) {
          const jobItem = bulkItems.find(
            (i) => i.productionoperationid === item.id && (i.multiresourceId ?? null) === (item.multiresourceId ?? null)
          );
          updateStateToUnassigned(jobItem, item.resourceOneId);
        }
        setSelectedItems([]);
      });

      return;
    }

    const jobItem = JSON.parse(event.dataTransfer.getData('jobItem'));

    const resourceOneId = jobItem?.pufferResourceOneId;

    if (!resourceOneId) {
      addNotification({
        content: 'Az alapértelmezett erőforrás nem található.',
        type: NotificationType.ERROR,
      });
      return;
    }

    if (jobItem.multiresourceId) {
      productionService
        .updateProductionOperationMultiResource(resourceOneId, jobItem.multiresourceId)
        .then(() => updateStateToUnassigned(jobItem, resourceOneId));
    } else {
      productionService
        .updateProdOperationResource(jobItem.productionoperationid, resourceOneId)
        .then(() => updateStateToUnassigned(jobItem, resourceOneId));
    }
  };

  const onFeedbackClick = (data) => {
    setIsFeedbackDialogOpen(true);
    setFeedbackData({
      productionId: data.productionId,
      prodOperationId: data.productionoperationid,
      prodOperationLabel: data.label,
      createdBy: user.userId,
      itemCode: data.articleNumber,
      baseLine: data.customeroerderlinenum,
      companyId: data.companyId,
    });
  };

  const getSourceListAttachment = (itemCode) => {
    setIsAttachmentDialogOpen(true);
    sourcelistService.getSourcelistAttachmentByItemCode(itemCode).then((data) => {
      setAttachments([...data.sourceListAttachment, ...data.itemAttachment]);
    });
  };

  const pufferTotals = useMemo(() => {
    if (!prodOperations?.rows?.length) return [];

    const totals = new Map();

    for (const row of prodOperations.rows) {
      const id = row.resourceOneId;
      const found = autoCompleteData?.resourceOne?.find((r) => r.id === id);
      const name = found?.name || row.resourceOneName;
      const minutes = Number(row.plannedQty) || 0;

      if (totals.has(id)) {
        totals.get(id).minutes += minutes;
      } else {
        totals.set(id, { name, minutes });
      }
    }

    return Array.from(totals.values()).map((t) => ({
      resourceOneName: t.name,
      totalPlannedQty: Math.round((t.minutes / 60) * 100) / 100,
    }));
  }, [prodOperations.rows, autoCompleteData?.resourceOne]);

  const toggleItemSelection = (row) => {
    setSelectedItems((prev) => {
      const exists = prev.some(
        (r) =>
          r.productionoperationid === row.productionoperationid &&
          (r.multiresourceId ?? null) === (row.multiresourceId ?? null)
      );
      if (exists) {
        return prev.filter(
          (r) =>
            !(
              r.productionoperationid === row.productionoperationid &&
              (r.multiresourceId ?? null) === (row.multiresourceId ?? null)
            )
        );
      }
      return [...prev, row];
    });
  };

  const operationFilterItem = query.filter.filterModel.items.find((item) => item.field === 'operationId');

  const selectedOperationValues = operationFilterItem?.value ?? [];

  const selectedOperationOptions = Array.isArray(selectedOperationValues)
    ? autocompleteOptions.operations.filter((opt) => selectedOperationValues.includes(opt.value))
    : [];

  const handleSelectAllItem = (rows) => {
    for (const row of rows) {
      setSelectedItems((prev) => {
        const exists = prev.some(
          (r) =>
            r.productionoperationid === row.productionoperationid &&
            (r.multiresourceId ?? null) === (row.multiresourceId ?? null)
        );

        if (!exists) {
          return [...prev, row];
        }
        return prev;
      });
    }
  };

  const handleClearAllItem = (rows) => {
    setSelectedItems((prev) =>
      prev.filter(
        (item) =>
          !rows.some(
            (r) =>
              r.productionoperationid === item.productionoperationid &&
              (r.multiresourceId ?? null) === (item.multiresourceId ?? null)
          )
      )
    );
  };

  const loadMoreRenderedOperations = () => {
    if (chunkAppendScheduledRef.current) return;

    const total = prodOperations.rows?.length ?? 0;
    if (renderedOperationCount >= total) return;

    chunkAppendScheduledRef.current = true;
    requestAnimationFrame(() => {
      setRenderedOperationCount((prev) => Math.min(prev + RENDER_CHUNK_SIZE, total));
      chunkAppendScheduledRef.current = false;
    });
  };

  const onOperationListScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    const remaining = scrollHeight - (scrollTop + clientHeight);

    if (remaining <= 200) {
      loadMoreRenderedOperations();
    }
  };

  return (
    <div className="bg-[#E9F5FE] px-6">
      <div className="flex justify-between gap-5 flex-wrap">
        <div className="flex gap-x-5 flex-wrap mb-5">
          <div className="w-[320px]">
            <AutoCompleteSelectUI
              limitTags={1}
              label={<FormLabelUI text="Művelet kereső" />}
              dataset={autocompleteOptions.operations}
              onChange={(_e, newVal, reason) => {
                const normalized = reason === 'clear' || !newVal || newVal.length === 0 ? null : newVal;

                handleFilterChange('operationId', normalized);
                saveView('jobMonitorTaskAssigmentFilterV2', { operationId: newVal });
              }}
              selectedValue={selectedOperationOptions}
              freeSolo
              multiple
              getOptionLabel={(option) => option.label}
            />
          </div>
          <div className="w-[320px]">
            <AutoCompleteSelectUI
              limitTags={1}
              dataset={autocompleteOptions.resourceOne}
              label={<FormLabelUI text="Erőforrás kereső" />}
              onChange={(_e, newVal, reason) => {
                handleFilterChange('resourceOneId', reason === 'clear' ? null : newVal);
                saveView('jobMonitorTaskAssigmentFilterV2', { resourceOneId: newVal });
                setSelectedResources((prev) => ({
                  ...prev,
                  id: newVal,
                }));

                if (!newVal || newVal.length === 0) {
                  setFilteredResources([]);
                }
              }}
              selectedValue={selectedResources?.id}
              freeSolo
              multiple
              getOptionLabel={(option) => option.label}
            />
          </div>
          <div className="w-[200px]">
            <AutoCompleteSelectServiceUI
              label={<FormLabelUI text="Késztermék" />}
              service={itemAutocompleteService}
              serviceRequestBuilder={itemAutocompleteRequestBuilder}
              onChange={(_e, newVal, reason) => {
                handleFilterChange('finishedItemCode', reason === 'clear' ? null : newVal?.value);
              }}
              selectedValue={query.filter.filterModel.items.find((item) => item.field === 'finishedItemCode')?.value}
            />
          </div>
          <div className="w-[200px]">
            <DebouncedInputUI
              label="Vevőhivatkozási szám"
              debounceMs={800}
              setQuickFilterSearchValue={(newVal) => {
                handleFilterChange('reference', newVal);
                saveView('jobMonitorTaskAssigmentFilterV2', { reference: newVal });
              }}
              quickFilterSearchValue={query.filter.filterModel.items.find((item) => item.field === 'reference')?.value}
            />
          </div>
          <div className="w-[200px]">
            <DebouncedInputUI
              label="Alapanyag szűrés"
              debounceMs={800}
              quickFilterSearchValue={(() => {
                const code = jobMonitorTaskAssigmentFilterV2?.AlapanyagItemCode ?? '';
                const name = jobMonitorTaskAssigmentFilterV2?.AlapanyagItemName ?? '';
                if (code && name) return `${code} - ${name}`;
                if (code) return code;
                return '';
              })()}
              setQuickFilterSearchValue={(newVal) => {
                const parts = newVal?.split(' - ') ?? [];

                if (!newVal || newVal.trim() === '') {
                  handleFilterChange([
                    { field: 'AlapanyagItemCode', value: null },
                    { field: 'AlapanyagItemName', value: null },
                  ]);
                  saveView('jobMonitorTaskAssigmentFilterV2', {
                    AlapanyagItemCode: null,
                    AlapanyagItemName: null,
                  });
                } else if (parts.length === 2) {
                  handleFilterChange([
                    {
                      field: 'AlapanyagItemCode',
                      operator: 'contains',
                      value: parts[0],
                    },
                    {
                      field: 'AlapanyagItemName',
                      operator: 'contains',
                      value: parts[1],
                    },
                  ]);
                  saveView('jobMonitorTaskAssigmentFilterV2', {
                    AlapanyagItemCode: parts[0],
                    AlapanyagItemName: parts[1],
                  });
                } else {
                  handleFilterChange('AlapanyagItemCode', newVal);
                  handleFilterChange('AlapanyagItemName', null);
                  saveView('jobMonitorTaskAssigmentFilterV2', {
                    AlapanyagItemCode: newVal,
                    AlapanyagItemName: null,
                  });
                }
              }}
            />
          </div>
          <div className="pt-4">
            <SwitchUI
              label="Foly./Ellenőrzés"
              checked={check}
              onClick={() => {
                setCheck((prev) => !prev);
              }}
            />
          </div>
          <div className="pt-4">
            <SwitchUI
              label="Előző műv. bef."
              checked={prevStatus}
              onClick={() => {
                setPrevStatus((prev) => !prev);
                handleFilterChange('prevStatus', !prevStatus ? 'COMPLETED' : '');
              }}
            />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-3">
        <div
          id="scrollableDiv"
          className="col-span-4 min-h-[calc(100vh-135px)]  max-h-[calc(100vh-135px)] overflow-y-scroll custom-scrollbar bg-gray-100"
          onScroll={onOperationListScroll}
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDropToUnassigned}
        >
          {pufferTotals.length > 0 && (
            <div className="bg-white mb-3 p-2 text-labelColor flex items-center justify-between gap-2">
              <Typography fontWeight="bold" fontSize="large">
                {pufferTotals.map((t) => `${t.resourceOneName}: ${t.totalPlannedQty} óra`).join(', ')}
              </Typography>
              <div>
                <ButtonUI text="Mind" className="bg-primary" onClick={() => handleSelectAllItem(prodOperations.rows)} />
                <ButtonUI
                  text="Ürít"
                  className="bg-warning"
                  style={{ marginLeft: '10px' }}
                  onClick={() => handleClearAllItem(prodOperations.rows)}
                />
              </div>
            </div>
          )}

          {renderedProdOperations?.map((operation) => (
            <JobMonitorCard
              key={getRowKey(operation)}
              data={operation}
              handleNavigate={handleNavigate}
              getProductionById={getProductionById}
              setProdOperations={setProdOperations}
              params={params}
              onDragStart={onDragStart}
              setIsAttachmentDialogOpen={setIsAttachmentDialogOpen}
              setAttachments={setAttachments}
              autoCompleteData={autoCompleteData}
              onFeedbackClick={onFeedbackClick}
              getSourceListAttachment={getSourceListAttachment}
              isSelected={selectedItemKeys.has(getRowKey(operation))}
              onSelectToggle={toggleItemSelection}
            />
          ))}

          {renderedOperationCount < (prodOperations.rows?.length ?? 0) && (
            <div className="p-2 text-center text-sm text-gray-600">Tovabbi elemek megjelenitese gorgetessel...</div>
          )}
        </div>
        <div className="col-span-8 min-h-[calc(100vh-135px)] max-h-[calc(100vh-135px)] overflow-x-auto overflow-hidden custom-scrollbar">
          <div className="flex justify-start items-start gap-1 pt-0">
            {filteredResources?.map((resource) => (
              <div className="min-w-[350px] max-w-[350px]" key={resource.resourceOneId}>
                <JobMonitorResources
                  resource={resource}
                  getProductionById={getProductionById}
                  onDragStart={onDragStart}
                  onDrop={onDrop}
                  isDragging={{ draggingItemId, setDraggingItemId }}
                  dragOverData={{ dragOverIndex, setDragOverIndex }}
                  setIsAttachmentDialogOpen={setIsAttachmentDialogOpen}
                  setAttachments={setAttachments}
                  autoCompleteData={autoCompleteData}
                  onFeedbackClick={onFeedbackClick}
                  getSourceListAttachment={getSourceListAttachment}
                  onSelectToggle={toggleItemSelection}
                  selectedItems={selectedItems}
                  handleSelectAllItem={handleSelectAllItem}
                  handleClearAllItem={handleClearAllItem}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      {productDialogIsOpen && (
        <ProductDialog
          open={productDialogIsOpen}
          handleClose={() => setProductDialogIsOpen(false)}
          production={production}
          changed={{ productionChanged, setProductionChanged }}
        />
      )}
      <AttachmentDialogUI
        open={isAttachmentDialogOpen}
        data={attachments}
        handleClose={() => setIsAttachmentDialogOpen(false)}
      />
      {isFeedBackDialogOpen && (
        <TaskFeedbackDialog
          open={isFeedBackDialogOpen}
          handleClose={() => setIsFeedbackDialogOpen(false)}
          data={feedbackData}
        />
      )}
    </div>
  );
};
